import { COLORS } from '@/config/colors';
import { css, fillOutlined, circle, shade } from './draw-utils';
import type { ProceduralDef } from '../types';

/**
 * B1 Sentinel Orb parts — docs/02-bosses.md.
 * Body + rotating orbit ring + support drone.
 */
export const bossSentinelBody: ProceduralDef = {
  key: 'boss/sentinel/body',
  width: 192,
  height: 192,
  anchorX: 0.5,
  anchorY: 0.5,
  frames: 1,
  draw(ctx, w, h) {
    const c = w / 2;
    const R = c - 14;
    const steel = 0x4a4256;

    // Outer armor sphere
    circle(ctx, c, c, R);
    fillOutlined(ctx, steel, 8);

    // Top-left highlight crescent
    ctx.save();
    ctx.beginPath();
    ctx.arc(c - R * 0.15, c - R * 0.15, R * 0.85, 0, Math.PI * 2);
    ctx.clip();
    circle(ctx, c - R * 0.3, c - R * 0.3, R * 0.75);
    ctx.fillStyle = css(shade(steel, 1.2));
    ctx.fill();
    ctx.restore();

    // Armor plates (6 segment lines)
    ctx.strokeStyle = css(shade(steel, 0.65));
    ctx.lineWidth = 4;
    for (let i = 0; i < 6; i++) {
      const a = (Math.PI / 3) * i + 0.3;
      ctx.beginPath();
      ctx.moveTo(c + Math.cos(a) * R * 0.45, c + Math.sin(a) * R * 0.45);
      ctx.lineTo(c + Math.cos(a) * R * 0.92, c + Math.sin(a) * R * 0.92);
      ctx.stroke();
    }

    // Center eye — orange core (takes damage from anywhere, B1 tutorial)
    circle(ctx, c, c, R * 0.42);
    fillOutlined(ctx, COLORS.dangerOrange, 6);
    circle(ctx, c, c, R * 0.26);
    ctx.fillStyle = css(shade(COLORS.dangerOrange, 1.35));
    ctx.fill();
    circle(ctx, c - R * 0.08, c - R * 0.1, R * 0.09);
    ctx.fillStyle = 'rgba(255, 240, 220, 0.95)';
    ctx.fill();

    // Perimeter rivets
    ctx.fillStyle = css(shade(steel, 0.55));
    for (let i = 0; i < 8; i++) {
      const a = (Math.PI / 4) * i;
      circle(ctx, c + Math.cos(a) * R * 0.97, c + Math.sin(a) * R * 0.97, 4);
      ctx.fill();
    }
    void h;
  },
};

/** Orbit ring — rotates around the body (code supplies scale/rotation). */
export const bossSentinelRing: ProceduralDef = {
  key: 'boss/sentinel/orbit-ring',
  width: 256,
  height: 256,
  anchorX: 0.5,
  anchorY: 0.5,
  frames: 1,
  draw(ctx, w, h) {
    const c = w / 2;
    const R = c - 10;

    ctx.beginPath();
    ctx.arc(c, c, R, 0, Math.PI * 2);
    ctx.strokeStyle = css(0x2a2440);
    ctx.lineWidth = 10;
    ctx.stroke();
    ctx.beginPath();
    ctx.arc(c, c, R, 0, Math.PI * 2);
    ctx.strokeStyle = css(0x4a4256);
    ctx.lineWidth = 5;
    ctx.stroke();

    // 4 energy nodes on the ring
    for (let i = 0; i < 4; i++) {
      const a = (Math.PI / 2) * i;
      circle(ctx, c + Math.cos(a) * R, c + Math.sin(a) * R, 9);
      fillOutlined(ctx, COLORS.dangerOrange, 4);
    }
    void h;
  },
};

/** Support drone — small steel orb, red eye. */
export const bossSentinelDrone: ProceduralDef = {
  key: 'boss/sentinel/drone',
  width: 48,
  height: 48,
  anchorX: 0.5,
  anchorY: 0.5,
  frames: 1,
  draw(ctx, w, h) {
    const c = w / 2;
    const R = c - 6;
    circle(ctx, c, c, R);
    fillOutlined(ctx, 0x4a4256, 4.5);
    circle(ctx, c - R * 0.25, c - R * 0.25, R * 0.4);
    ctx.fillStyle = css(shade(0x4a4256, 1.25));
    ctx.fill();
    // Red eye — nose +X
    circle(ctx, c + R * 0.35, c, R * 0.32);
    fillOutlined(ctx, COLORS.dangerRed, 3);
    circle(ctx, c + R * 0.4, c - 2, 2.5);
    ctx.fillStyle = 'rgba(255,255,255,0.9)';
    ctx.fill();
    void h;
  },
};
